<template>
  <div class="interface-area">
    <WindowModal :title="modalTitle" type="Global">
      <template #modal-content>
        <ConfirmSwap
          ref="confirmSwap"
          :swap="swap"
          :pair-data="pairData"
          :input-method="inputMethod"
          :transaction="transaction"
          @swapTokens="swapTokens"
          @initConfirmState="changeModalTitle('init')"
          @initLoading="changeModalTitle('loading')"
          @initSuccess="changeModalTitle('success')"
          @initError="changeModalTitle('error')"
          @closeGlobalModal="closeModal"
          @priceRefresh="refreshPrice"
        />
      </template>
    </WindowModal>
    <div :class="{'chart-box': true, 'active': openChart}">
      <div class="header">
        <div v-if="fromToken !== null && toToken !== null && pairData !== null" class="left-area">
          <div class="icons">
            <nuxt-img
              :src="fromToken.address !== $config.ZERO ? $getImageByAddress(fromToken.address) : $getImage(fromToken.logoURI)"
              :alt="fromToken.name"
              :title="fromToken.symbol"
              placeholder
              :lazy="true"
              @error="$event.target.src = require('~/static/img/default_token.svg')"
            />
            <nuxt-img
              :src="toToken.address !== $config.ZERO ? $getImageByAddress(toToken.address) : $getImage(toToken.logoURI)"
              :alt="toToken.name"
              :title="toToken.symbol"
              placeholder
              :lazy="true"
              @error="$event.target.src = require('~/static/img/default_token.svg')"
            />
          </div>
          <div class="title">
            <p>
              {{ fromToken.symbol }}/{{ toToken.symbol }}
            </p>
          </div>
          <div class="icon-area">
            <Swap />
          </div>
        </div>
        <div v-else class="left-area">
          <div class="icons">
            <Skeleton circle width="24px" height="24px" style="margin-right: .25rem" />
            <Skeleton circle width="24px" height="24px" />
          </div>
          <div class="title">
            <p>
              <Skeleton width="75px" height="25px" />
            </p>
          </div>
        </div>
        <div class="right-area"></div>
      </div>
      <div class="details">
        <div v-if="pairData !== null" class="left-area">
          <div class="price">
            <div class="h2">
              {{ lineChart !== null ? lineChart.data.datasets[0].data[6] : '0.00' }}
            </div>
            <div v-if="fromToken !== null && toToken !== null" class="h4">
              {{ fromToken.symbol }}/{{ toToken.symbol }}
            </div>
            <div v-else class="h4">
              .../...
            </div>
            <div class="h4 plus">
              {{ lineChart !== null ? '+0.590 (0.73%)' : '~0.00 (0.00%)' }}
            </div>
          </div>
          <div class="date">
            {{ $beautifyDate('2023-01-12 08:00:00', true) }}
          </div>
        </div>
        <div v-else class="left-area">
          <div class="price">
            <div class="h2">
              <Skeleton width="75px" height="35px" />
            </div>
            <div class="h4">
              <Skeleton width="175px" height="22px" />
            </div>
          </div>
          <div class="date">
            <Skeleton width="130px" height="16px" />
          </div>
        </div>
        <div v-if="pairData !== null" class="right-area">
          <div class="date-pips">
            <div :class="{'pip': true, 'active': dateFilter === 1}" @click="setDateFilter(1)">
              24{{ $t('common.h') }}
            </div>
            <div :class="{'pip': true, 'active': dateFilter === 7}" @click="setDateFilter(7)">
              1{{ $t('common.w') }}
            </div>
            <div :class="{'pip': true, 'active': dateFilter === 30}" @click="setDateFilter(30)">
              1{{ $t('common.m') }}
            </div>
            <div :class="{'pip': true, 'active': dateFilter === 365}" @click="setDateFilter(365)">
              1{{ $t('common.y') }}
            </div>
          </div>
        </div>
        <div v-else class="right-area">
          <div class="date-pips">
            <Skeleton width="200px" height="32px" />
          </div>
        </div>
      </div>
      <div v-if="pairData !== null && lineChart !== null && !loadingChart" class="chart-container">
        <LineChart ref="chart" :chart-data="lineChart.data" :options="lineChart.options" />
      </div>
      <div v-else-if="loadingChart || pairData === null" class="chart-container">
        <Loading />
        <div class="h6">
          {{ $t('analytics.loading_chart_data') }}
        </div>
      </div>
      <div v-else class="chart-container">
        <CloudRefreshIcon />
        <div class="h6">
          {{ $t('analytics.no_chart_data') }}
        </div>
      </div>
    </div>
    <div :class="{'swap-box': true, 'shorten': openChart}">
      <div class="header-area">
        <h1>
          {{ $t('swap.h1') }}
        </h1>
        <div ref="settingsButton" class="settings">
          <div :class="{'toggler static': true, 'active': openChart}" @click="toggleChart">
            <ChartApr />
          </div>
          <div class="toggler" @click="toggleSettings">
            <Cog />
          </div>
        </div>
      </div>
      <div class="tokens-area">
        <div class="token-item">
          <div class="form-area">
            <div class="input-area">
              <label for="inputFrom">
                {{ $t('swap.from') }}
              </label>
              <input
                id="inputFrom"
                v-model="swap.from_value"
                type="number"
                min="0"
                step="0.0001"
                lang="en"
                placeholder="0.0..."
                @input="inputFrom"
              >
              <div :class="{'max-button': true, 'active': swap.from_token.balance !== undefined && parseFloat(swap.from_token.balance) > 0}" @click="setFromMax">
                <p class="text">
                  {{ $t('swap.max') }}
                </p>
              </div>
            </div>
            <div ref="fromListButton" class="token-select" @click="toggleFromList">
              <p class="desc">
                {{ $t('liquidity.select_token') }}
              </p>
              <div v-if="fromToken === null" class="token-data">
                <div class="token-area">
                  <div class="empty-area"></div>
                  <p class="normal">
                    {{ $t('swap.pick') }}
                  </p>
                </div>
                <div class="chevron-area">
                  <ChevronDown />
                </div>
              </div>
              <div v-else class="token-data">
                <div class="token-area">
                  <nuxt-img
                    :src="fromToken.address !== $config.ZERO ? $getImageByAddress(fromToken.address) : $getImage(fromToken.logoURI)"
                    :alt="fromToken.name"
                    :title="fromToken.symbol"
                    placeholder
                    :lazy="true"
                    @error="$event.target.src = require('~/static/img/default_token.svg')"
                  />
                  <p>
                    {{ fromToken.symbol }}
                  </p>
                </div>
                <div class="chevron-area">
                  <ChevronDown />
                </div>
              </div>
            </div>
          </div>
          <div ref="fromList" class="token-select-list">
            <div class="h4">
              {{ $t('swap.select_token') }}
            </div>
            <form class="search-area" method="POST">
              <button type="button" class="search-button" :arira-label="$t('common.search')">
                <Search />
              </button>
              <input v-model="search" type="text" :placeholder="$t('common.search') + '...'" @change="toggleTokensList">
            </form>
            <client-only>
              <TokensList :current-token="swap.from_token" :pair-token="swap.to_token" :list="computedList" @pick="selectFromToken" />
            </client-only>
            <div ref="manageFromButton" class="open-manage" @click="toggleManageFrom">
              {{ $t('swap.manage') }}
            </div>
          </div>
          <ManageModal ref="manageFromArea" :active="activeManageFrom" @toggle="toggleManageFrom" />
          <div :class="{'copy-area': true, 'active': swap.from_token.address.length > 0 && swap.from_token.address !== $config.ZERO}">
            <div class="svg-area" :tooltip="$t('common.copy')" @click="copyAddress($event, swap.from_token.address)">
              <Copy />
            </div>
            <div class="svg-area" :tooltip="$t('common.add_to_metamask')" @click="$addToMetamask(swap.from_token)">
              <MetamaskIcon />
            </div>
          </div>
          <p v-if="fromToken !== null && fromToken.address.length > 0" class="available">
            {{ $t('swap.available') }}:
            <span v-if="fromToken.balance !== undefined && fromToken.balance !== null" class="bold">{{ $formatCryptoAmount(fromToken.balance, 4) }}</span>
            <span v-else class="bold">0</span>
          </p>
        </div>
        <div class="swap-area">
          <div class="swap-button" @click="swapInputs">
            <Swap />
          </div>
        </div>
        <div class="token-item">
          <div class="form-area">
            <div class="input-area">
              <label for="inputTo">
                {{ $t('swap.to') }}
              </label>
              <input
                id="inputTo"
                v-model="swap.to_value"
                type="number"
                min="0"
                step="0.0001"
                lang="en"
                placeholder="0.0..."
                @input="inputTo"
              >
              <div :class="{'max-button': true, 'active': swap.to_token.balance !== undefined && parseFloat(swap.to_token.balance) > 0}" @click="setToMax">
                <p class="text">
                  {{ $t('swap.max') }}
                </p>
              </div>
            </div>
            <div ref="toListButton" class="token-select" @click="toggleToList">
              <p class="desc">
                {{ $t('liquidity.select_token') }}
              </p>
              <div v-if="toToken === null" class="token-data">
                <div class="token-area">
                  <div class="empty-area"></div>
                  <p class="normal">
                    {{ $t('swap.pick') }}
                  </p>
                </div>
                <div class="chevron-area">
                  <ChevronDown />
                </div>
              </div>
              <div v-else class="token-data">
                <div class="token-area">
                  <nuxt-img
                    :src="toToken.address !== $config.ZERO ? $getImageByAddress(toToken.address) : $getImage(toToken.logoURI)"
                    :alt="toToken.name"
                    :title="toToken.symbol"
                    placeholder
                    :lazy="true"
                    @error="$event.target.src = require('~/static/img/default_token.svg')"
                  />
                  <p>
                    {{ toToken.symbol }}
                  </p>
                </div>
                <div class="chevron-area">
                  <ChevronDown />
                </div>
              </div>
            </div>
          </div>
          <div ref="toList" class="token-select-list">
            <div class="h4">
              {{ $t('swap.select_token') }}
            </div>
            <form class="search-area" method="POST">
              <button type="button" class="search-button" :arira-label="$t('common.search')">
                <Search />
              </button>
              <input v-model="search" type="text" :placeholder="$t('common.search') + '...'" @change="toggleTokensList">
            </form>
            <client-only>
              <TokensList :current-token="swap.to_token" :pair-token="swap.to_token" :list="computedList" @pick="selectToToken" />
            </client-only>
            <div ref="manageToButton" class="open-manage" @click="toggleManageTo">
              {{ $t('swap.manage') }}
            </div>
          </div>
          <ManageModal ref="manageToArea" :active="activeManageTo" @toggle="toggleManageTo" />
          <div :class="{'copy-area': true, 'active': swap.to_token.address.length > 0 && swap.to_token.address !== $config.ZERO}">
            <div class="svg-area" @click="copyAddress($event, swap.to_token.address)">
              <Copy />
            </div>
            <div class="svg-area" @click="$addToMetamask(swap.to_token)">
              <MetamaskIcon />
            </div>
          </div>
          <p v-if="toToken !== null && toToken.address.length > 0" class="available">
            {{ $t('swap.available') }}:
            <span v-if="toToken.balance !== undefined && toToken.balance !== null" class="bold">{{ $formatCryptoAmount(toToken.balance, 4) }}</span>
            <span v-else class="bold">0</span>
          </p>
        </div>
      </div>
      <div class="tolerance">
        <p>
          {{ $t('swap.slippage') }}: <span>{{ slippageTolerance }}</span>%
        </p>
      </div>
      <div :class="{'warning-area': true, 'active': errorText.length > 0}">
        <div class="icon-area">
          <Warn />
        </div>
        <div class="text-area">
          {{ errorText }}
        </div>
      </div>
      <div class="buttons-area">
        <button
          v-if="user !== undefined && user !== null"
          type="button"
          :class="{ 'btn cyan': true, 'disabled': !tokensPicked || swapCtaDisabled || loading || swapInsufficient }"
          :disabled="!tokensPicked || swapCtaDisabled || loading || swapInsufficient"
          @click="prepareSwap"
        >
          <span v-if="!loading">
            {{ swapCta }}
          </span>
          <span v-else>
            <Loading />
          </span>
        </button>
        <button v-else class="btn cyan" @click="openConnectModal">
          {{ $t('common.connect_wallet') }}
        </button>
      </div>
      <div v-if="!openChart" class="coins-left">
        <picture class="picture">
          <source srcset="/img/new/swap_coins-left.webp" type="image/webp">
          <nuxt-img
            fit="inside"
            width="250"
            src="/img/new/swap_coins-left.webp"
            alt="Swap blockchain coins on Defi Pool Finance"
            title="Swap coins"
          />
        </picture>
      </div>
      <div v-if="!openChart" class="coins-right">
        <picture class="picture">
          <source srcset="/img/new/swap_coins-right.webp" type="image/webp">
          <nuxt-img
            fit="inside"
            width="229"
            src="/img/new/swap_coins-right.webp"
            alt="Swap blockchain coins on Defi Pool Finance"
            title="Swap coins"
          />
        </picture>
      </div>
    </div>
    <div class="cyan-cloud">
      <CloudCyanLiquidityLeft />
    </div>
    <div class="cyan-cloud2">
      <CloudCyanLiquidityRight />
    </div>
  </div>
</template>

<script>
import { setTimeout } from 'timers'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { Skeleton } from 'vue-loading-skeleton'

import {
  hydrateWhenIdle,
  hydrateWhenVisible
} from 'vue-lazy-hydration'

import ChartApr from '~/components/svg/new/ChartApr'
import ConfirmSwap from '~/components/app/interface/ConfirmSwap'
import Swap from '~/components/svg/new/Swap'

export default {
  name: 'SwapComponent',
  components: {
    ChartApr,
    ChevronDown: hydrateWhenIdle(() => import('~/components/svg/new/ChevronDown')),
    CloudCyanLiquidityLeft: hydrateWhenIdle(() => import('~/components/svg/new/clouds/CloudCyanLiquidityLeft')),
    CloudCyanLiquidityRight: hydrateWhenIdle(() => import('~/components/svg/new/clouds/CloudCyanLiquidityRight')),
    CloudRefreshIcon: hydrateWhenVisible(() => import('~/components/svg/new/CloudRefreshIcon')),
    Cog: hydrateWhenIdle(() => import('~/components/svg/new/Cog')),
    ConfirmSwap,
    Copy: hydrateWhenVisible(() => import('~/components/svg/new/Copy')),
    LineChart: hydrateWhenIdle(() => import('~/components/analytics/parts/LineChart')),
    Loading: hydrateWhenVisible(() => import('~/components/svg/new/Loading')),
    ManageModal: hydrateWhenIdle(() => import('~/components/modals/ManageModal')),
    MetamaskIcon: hydrateWhenVisible(() => import('~/components/svg/MetamaskIcon')),
    Search: hydrateWhenVisible(() => import('~/components/svg/new/Search')),
    Swap,
    Skeleton,
    Warn: hydrateWhenVisible(() => import('~/components/svg/new/Warn')),
    TokensList: hydrateWhenIdle(() => import('~/components/app/interface/TokensList')),
    WindowModal: hydrateWhenIdle(() => import('~/components/modals/WindowModal'))
  },
  layout: 'new',
  data() {
    return {
      walletConnected: false,
      modalTitle: this.$t('swap.confirm_swap'),
      currentAccount: this.$t('common.connect_wallet'),
      tab: 'list',
      methodsAvailable: ['swapExactETHForTokens', 'swapETHForExactTokens', 'swapTokensForExactETH', 'swapExactTokensForETH', 'swapExactTokensForTokens', 'swapTokensForExactTokens'],
      search: '',
      errorText: '',
      swap: {
        from_value: null,
        from_token: {
          symbol: 'BNB',
          address: '0x0000000000000000000000000000000000000000',
          decimals: 18
        },
        to_value: null,
        to_token: {
          symbol: '',
          address: '',
          decimals: 18
        },
        method: 'swapExactETHForTokens'
      },
      pairData: null,
      dateFilter: 1,
      transaction: {
        gasFee: 3000000,
        tx: '',
        loading: false,
        pending: false
      },
      inputMethod: 'from',
      swapCta: this.$t('swap.cta'),
      swapCtaDisabled: true,
      swapInsufficient: false,
      loading: false,
      activeManageFrom: false,
      activeManageTo: false,
      openChart: false,
      loadingChart: false,
      lineChart: null
    }
  },
  async fetch({ store, error, route }) {
    try {
      const pageName = route.name.split('___')
      const seoData = store.getters['seos/seosByUri'](pageName[0])
      if (seoData === null) {
        await store.dispatch('seos/fetchSeos', {
          uri: route.name
        })
      }
    } catch (err) {
      error(err)
    }
  },
  head({ $config: { appUrl } }) {
    return {
      title: this.meta ? this.meta.title : this.$t('common.launching'),
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.meta ? this.meta.description : ''
        },
        {
          hid: 'og-description',
          name: 'og:description',
          content: this.meta ? this.meta.description : ''
        },
        {
          hid: 'og-title',
          name: 'og:title',
          content: this.meta
            ? this.meta.title
            : this.$t('common.launching')
        },
        {
          hid: 'og-image',
          name: 'og:image',
          content: this.meta
            ? this.meta.image
            : appUrl + 'seo/default.jpg'
        },
        {
          hid: 'keywords',
          name: 'keywords',
          content: this.meta ? this.meta.keyword : ''
        }
      ]
    }
  },
  computed: {
    ...mapGetters(['darkTheme']),
    ...mapGetters('auth', [
      'user'
    ]),
    ...mapGetters('seos', [
      'seosByUri'
    ]),
    meta() {
      const pageName = this.$nuxt.$route.name.split('___')
      return this.seosByUri(pageName[0])
    },
    fromToken() {
      const token = this.$store.getters['tokens/list'].length > 0 && this.swap.from_token.symbol.length > 0
        ? this.$store.getters['tokens/list'].find(item => item.symbol.toLowerCase() === this.swap.from_token.symbol.toLowerCase())
        : null
      return token !== undefined ? token : this.$store.getters['tokens/list'][0]
    },
    toToken() {
      const token = this.$store.getters['tokens/list'].length > 1 && this.swap.to_token.symbol.length > 0
        ? this.$store.getters['tokens/list'].find(item => item.symbol.toLowerCase() === this.swap.to_token.symbol.toLowerCase())
        : null
      return token !== undefined ? token : this.$store.getters['tokens/list'][1]
    },
    computedList() {
      return this.$store.getters['tokens/list'].filter(item => item.name.toLowerCase().includes(this.search.toLowerCase()) || item.symbol.toLowerCase().includes(this.search.toLowerCase()))
    },
    tokensPicked() {
      const picked = this.swap.from_token.address.length > 0 && this.swap.to_token.address.length > 0 &&
        this.swap.from_value !== null && this.swap.to_value !== null &&
        this.swap.from_value > 0 && this.swap.to_value > 0 && this.walletConnected
      if (!this.wrapAction && !this.unwrapAction) {
        return picked && this.pairData !== null
      } else {
        return picked
      }
    },
    wrapAction() {
      return this.swap.from_token.symbol.toLowerCase() === 'bnb' && this.swap.to_token.symbol.toLowerCase() === 'wbnb'
    },
    unwrapAction() {
      return this.swap.to_token.symbol.toLowerCase() === 'bnb' && this.swap.from_token.symbol.toLowerCase() === 'wbnb'
    },
    ...mapGetters('settings', [
      'slippageTolerance',
      'deadline',
      'expertMode',
      'multihops'
    ])
  },
  mounted() {
    const userData = this.$cookies.get(this.$config.userStorageName)
    if (userData !== undefined && userData.address !== undefined && this.$isAddress(userData.address)) {
      this.currentAccount = this.$t('common.connected_as') + ' ' + this.$shortenAddress(userData.address)
      this.walletConnected = true
    }
    this.$nuxt.$emit('tokenListToggle')
    this.$nuxt.$on('tokensLoaded', this.loadQueryTokens)
    window.addEventListener('click', this.handleClicks)
    if (this.computedList.length > 1) {
      this.loadQueryTokens()
    }
  },
  beforeDestroy() {
    window.removeEventListener('click', this.handleClicks)
  },
  methods: {
    ...mapActions('modal', [
      'changeModalSettingsState',
      'changeModalGlobalState',
      'changeModalWalletState'
    ]),
    ...mapActions('tokens', [
      'addToken'
    ]),
    ...mapMutations('tokens', [
      'changeBalance'
    ]),
    openConnectModal() {
      this.changeModalWalletState({
        state: true
      })
    },
    toggleTokensList() {
      this.$nuxt.$emit('tokenListToggle')
    },
    loadQueryTokens() {
      const fromAddress = this.$route.query.from
      const toAddress = this.$route.query.to
      if (fromAddress !== undefined && fromAddress !== null) {
        const tokenFrom = this.computedList.find(tkn => tkn.address === fromAddress)
        if (tokenFrom === undefined || tokenFrom === null) {
          this.importAndSelectToken(fromAddress, 'from')
        } else {
          this.selectFromToken(tokenFrom, false)
        }
      }
      if (toAddress !== undefined && toAddress !== null) {
        const tokenTo = this.computedList.find(tkn => tkn.address === toAddress)
        if (tokenTo === undefined || tokenTo === null) {
          this.importAndSelectToken(toAddress, 'to')
        } else {
          this.selectToToken(tokenTo, false)
        }
      }
      window.scrollTo(0, 0)
      if (this.$refs.confirmSwap !== undefined) {
        this.changeModalGlobalState({
          state: false
        })
        this.$refs.confirmSwap.returnInitState()
      }
    },
    async importAndSelectToken(address, type = 'from') {
      let addressCheck = address
      if (this.$isChecksumAddress(addressCheck)) {
        addressCheck = await window.eth.utils.toChecksumAddress(addressCheck)
      }
      const userAddress = await this.$getUserAddress()
      this.loading = true
      try {
        const ERC20File = await import('~/assets/abis/ERC20.json')
        const ERC20 = ERC20File.default
        const erc20Contract = new window.eth.eth.Contract(ERC20, addressCheck)
        const symbol = await erc20Contract.methods.symbol().call()
        const name = await erc20Contract.methods.name().call()
        const decimals = await erc20Contract.methods.decimals().call()
        const balance = parseFloat(window.eth.utils.fromWei(await erc20Contract.methods.balanceOf(userAddress).call({ from: userAddress })))
        const logoURI = this.$getImageByGeckoId(name.toLowerCase().replace(' ', '-'))
        const newToken = {
          bid: this.$config.chainId,
          id: 'import',
          address: addressCheck,
          name,
          symbol,
          decimals,
          logoURI,
          balance
        }
        await this.addToken(newToken)
        if (type === 'from') {
          this.selectFromToken(newToken, false)
        } else {
          this.selectToToken(newToken, false)
        }
        this.loading = false
      } catch (err) {
        this.loading = false
        console.log(err)
      }
    },
    changeWallet(newWallet) {
      this.currentAccount = this.$t('common.connected_as') + ' ' + newWallet
      this.walletConnected = true
    },
    toggleSettings() {
      this.changeModalSettingsState({
        state: true
      })
    },
    toggleChart() {
      this.openChart = !this.openChart
    },
    toggleFromList() {
      if (this.$refs.fromList !== undefined) {
        this.$refs.fromList.classList.toggle('active')
        this.$refs.fromListButton.classList.toggle('active')
        if (this.$refs.fromList.classList.contains('active')) {
          this.toggleTokensList()
        }
        this.search = ''
      }
    },
    toggleToList() {
      if (this.$refs.toList !== undefined) {
        this.$refs.toList.classList.toggle('active')
        this.$refs.toListButton.classList.toggle('active')
        if (this.$refs.toList.classList.contains('active')) {
          this.toggleTokensList()
        }
        this.search = ''
      }
    },
    async selectFromToken(token) {
      if (this.swap.to_token.symbol.toLowerCase() !== token.symbol.toLowerCase()) {
        this.swap.from_token = token
        this.pairData = null
        this.toggleFromList()
        if (this.$isStableCurrency(this.swap.from_token.symbol)) {
          this.swap.method = 'swapExactETHForTokens'
        } else if (!this.$isStableCurrency(this.swap.to_token.symbol)) {
          this.swap.method = 'swapExactTokensForTokens'
        }
        this.loading = true
        if (!this.wrapAction && !this.unwrapAction) {
          try {
            this.pairData = await this.$loadPairData(this.swap.from_token, this.swap.to_token)
            this.checkLiquidity()
            this.loadChart()
          } catch (err) {
            console.error(err)
            this.$nuxt.$emit('openError', this.$t('swap.no_pair_header'), this.$t('swap.no_pair'))
          }
        } else if (this.wrapAction) {
          this.swapCta = this.$t('swap.wrap')
          this.swapCtaDisabled = false
        } else {
          this.swapCta = this.$t('swap.unwrap')
          this.swapCtaDisabled = false
        }
        const chainId = await window.eth.eth.net.getId()
        if (!this.$isChainSupported(chainId)) {
          this.loading = false
          return
        }
        try {
          const userAddress = await this.$getUserAddress()
          const ERC20File = await import('~/assets/abis/ERC20.json')
          const ERC20 = ERC20File.default
          const tokenContract = this.swap.from_token.address !== this.$config.ZERO ? new window.eth.eth.Contract(ERC20, this.swap.from_token.address) : null
          if (tokenContract !== null) {
            const balanceFrom = await tokenContract.methods.balanceOf(userAddress).call({ from: userAddress })
            const decimals = await tokenContract.methods.decimals().call({ from: userAddress })
            const currentDecimal = this.$getDecimalsName(decimals)
            let balance = parseFloat(window.eth.utils.fromWei(balanceFrom, currentDecimal.name))
            if (currentDecimal.multiply > 0) {
              balance /= 10 ** currentDecimal.multiply
            }
            this.changeBalance({
              address: this.swap.from_token.address,
              balance
            })
          } else {
            this.changeBalance({
              address: this.swap.from_token.address,
              balance: parseFloat(window.eth.utils.fromWei(await window.eth.eth.getBalance(userAddress)))
            })
          }
        } catch (err) {
          console.log(err)
        }

        this.loading = false
      }
    },
    async selectToToken(token) {
      if (this.swap.from_token.symbol.toLowerCase() !== token.symbol.toLowerCase()) {
        this.swap.to_token = token
        this.pairData = null
        this.toggleToList()
        if (this.$isStableCurrency(this.swap.to_token.symbol)) {
          this.swap.method = 'swapTokensForExactETH'
        } else if (!this.$isStableCurrency(this.swap.from_token.symbol)) {
          this.swap.method = 'swapTokensForExactTokens'
        }
        this.loading = true
        if (!this.wrapAction && !this.unwrapAction) {
          try {
            this.pairData = await this.$loadPairData(this.swap.from_token, this.swap.to_token)
            this.checkLiquidity()
            this.loadChart()
          } catch (err) {
            console.error(err)
            this.$nuxt.$emit('openError', this.$t('swap.no_pair_header'), this.$t('swap.no_pair'))
          }
        } else if (this.wrapAction) {
          this.swapCta = this.$t('swap.wrap')
          this.swapCtaDisabled = false
        } else {
          this.swapCta = this.$t('swap.unwrap')
          this.swapCtaDisabled = false
        }
        const chainId = await window.eth.eth.net.getId()
        if (!this.$isChainSupported(chainId)) {
          this.loading = false
          return
        }
        try {
          const userAddress = await this.$getUserAddress()
          const ERC20File = await import('~/assets/abis/ERC20.json')
          const ERC20 = ERC20File.default
          const tokenContract = this.swap.to_token.address !== this.$config.ZERO ? new window.eth.eth.Contract(ERC20, this.swap.to_token.address) : null
          if (tokenContract !== null) {
            const balanceFrom = await tokenContract.methods.balanceOf(userAddress).call({ from: userAddress })
            const decimals = await tokenContract.methods.decimals().call({ from: userAddress })
            const currentDecimal = this.$getDecimalsName(decimals)
            let balance = parseFloat(window.eth.utils.fromWei(balanceFrom, currentDecimal.name))
            if (currentDecimal.multiply > 0) {
              balance /= 10 ** currentDecimal.multiply
            }
            this.changeBalance({
              address: this.swap.to_token.address,
              balance
            })
          } else {
            this.changeBalance({
              address: this.swap.to_token.address,
              balance: parseFloat(window.eth.utils.fromWei(await window.eth.eth.getBalance(userAddress)))
            })
          }
        } catch (err) {
          console.log(err)
        }

        this.loading = false
      }
    },
    toggleManageFrom() {
      this.activeManageFrom = !this.activeManageFrom
      this.importManage = ''
    },
    toggleManageTo() {
      this.activeManageTo = !this.activeManageTo
      this.importManage = ''
    },
    setSettings(newSettings) {
      this.settings = newSettings
    },
    inputFrom() {
      if (this.pairData !== null && this.pairData.constantProduct !== 0) {
        const toTokenPrice = this.pairData.reserves.reserve1 / this.pairData.reserves.reserve0 * this.swap.from_value
        this.swap.to_value = this.$formatCryptoAmount(toTokenPrice, 10, true)
      } else if (this.wrapAction || this.unwrapAction) {
        this.swap.to_value = this.swap.from_value
      }
      this.changeMethodOnInput('from')
    },
    inputTo() {
      if (this.pairData !== null && this.pairData.constantProduct !== 0) {
        const fromTokenPrice = this.pairData.reserves.reserve0 / this.pairData.reserves.reserve1 * this.swap.to_value
        this.swap.from_value = this.$formatCryptoAmount(fromTokenPrice, 10, true)
      } else if (this.wrapAction || this.unwrapAction) {
        this.swap.from_value = this.swap.to_value
      }
      this.changeMethodOnInput('to')
    },
    changeMethodOnInput(inputType) {
      this.inputMethod = inputType
      this.checkInsufficientFunds()
      if (!this.wrapAction && !this.unwrapAction) {
        this.checkLiquidity()
      }
      if (inputType === 'from') {
        if (this.$isStableCurrency(this.swap.from_token.symbol)) {
          this.swap.method = 'swapExactETHForTokens'
        } else if (this.$isStableCurrency(this.swap.to_token.symbol)) {
          this.swap.method = 'swapExactTokensForETH'
        } else {
          this.swap.method = 'swapExactTokensForTokens'
        }
      }
      if (inputType === 'to') {
        if (this.$isStableCurrency(this.swap.to_token.symbol)) {
          this.swap.method = 'swapTokensForExactETH'
        } else if (this.$isStableCurrency(this.swap.from_token.symbol)) {
          this.swap.method = 'swapETHForExactTokens'
        } else {
          this.swap.method = 'swapTokensForExactTokens'
        }
      }
    },
    changeMethod(type = 'coin-token') {
      if (type === 'token-token') {
        if (this.swap.method === 'swapExactETHForTokens' ||
            this.swap.method === 'swapExactTokensForETH') {
          this.swap.method = 'swapExactTokensForTokens'
        } else if (this.swap.method === 'swapETHForExactTokens' ||
                   this.swap.method === 'swapTokensForExactETH') {
          this.swap.method = 'swapTokensForExactTokens'
        }
      } else if (type === 'coin-token') {
        if (this.swap.method === 'swapExactTokensForTokens' ||
            this.swap.method === 'swapExactTokensForETH') {
          this.swap.method = 'swapExactETHForTokens'
        } else if (this.swap.method === 'swapTokensForExactTokens' ||
                   this.swap.method === 'swapTokensForExactETH') {
          this.swap.method = 'swapETHForExactTokens'
        }
      } else if (type === 'token-coin') {
        if (this.swap.method === 'swapExactTokensForTokens' ||
            this.swap.method === 'swapExactETHForTokens') {
          this.swap.method = 'swapExactTokensForETH'
        } else if (this.swap.method === 'swapTokensForExactTokens' ||
                   this.swap.method === 'swapETHForExactTokens') {
          this.swap.method = 'swapTokensForExactETH'
        }
      }
    },
    clearForm() {
      this.swap = {
        from_value: null,
        from_token: {
          symbol: '',
          address: '',
          decimals: 18
        },
        to_value: null,
        to_token: {
          symbol: '',
          address: '',
          decimals: 18
        }
      }
    },
    swapInputs() {
      const fromValue = this.swap.from_value
      const fromToken = this.swap.from_token
      let toValue = this.swap.to_value
      this.swap = {
        from_token: this.swap.to_token,
        to_value: fromValue,
        to_token: fromToken
      }
      if (this.pairData !== null && this.pairData.constantProduct !== 0) {
        this.pairData = this.$switchPairData(this.pairData)
        toValue = this.pairData.reserves.reserve0 / this.pairData.reserves.reserve1 * fromValue
      }
      this.swap.from_value = toValue
      this.inputFrom()
    },
    setFromMax() {
      this.swap.from_value = this.swap.from_token.balance
      this.inputFrom()
    },
    setToMax() {
      this.swap.to_value = this.swap.to_token.balance
      this.inputTo()
    },
    async prepareSwap() {
      const chainOk = await this.$replaceChain()
      if (!chainOk) {
        return
      }
      if (this.wrapAction) {
        this.wrapToken()
      } else if (this.unwrapAction) {
        this.unwrapToken()
      } else if (this.tokensPicked) {
        this.$store.dispatch('modal/changeModalGlobalState', {
          state: true
        })
      }
    },
    async refreshPrice() {
      this.pairData = await this.$loadPairData(this.swap.from_token, this.swap.to_token)
    },
    async wrapToken() {
      this.transaction.loading = true
      this.transaction.pending = true
      this.loading = true
      const chainOk = await this.$replaceChain()
      if (!chainOk) {
        this.transaction.loading = false
        this.transaction.pending = false
        this.loading = false
        return
      }
      const userAddress = await this.$getUserAddress()
      const WBNBFile = await import('~/assets/abis/WBNB.json')
      const WBNB = WBNBFile.default
      const wbnbContract = new window.eth.eth.Contract(WBNB, this.$config.wbnbAddress)
      const stringNumber = Number(parseFloat(this.swap.from_value)).toPrecision(10)
      const valueWei = window.eth.utils.toBN(
        window.eth.utils.toWei(
          stringNumber
        )
      )
      try {
        const gasPrice = await this.$calculateGasCost()
        const gasFeeEstimated = await wbnbContract.methods.deposit()
          .estimateGas({ from: userAddress, value: valueWei, gas: this.transaction.gasFee })

        wbnbContract.methods.deposit()
          .send({ from: userAddress, value: valueWei, gas: Math.round(gasFeeEstimated * 1.15).toString(), gasPrice })
          .then((result) => {
            this.transaction.loading = false
            this.transaction.pending = false
            this.loading = false
            this.transaction.tx = result.transactionHash
            if (this.$refs.confirmSwap !== undefined) {
              this.$store.dispatch('modal/changeModalGlobalState', {
                state: true
              })
              this.$refs.confirmSwap.initSuccessScreen()
            }
            this.$saveTransaction({
              tid0: this.$config.wbnbAddress,
              t0_amount: this.swap.from_value,
              hash: result.transactionHash,
              gasUsed: result.gasUsed,
              transactionIndex: result.transactionIndex
            },
            this.$t('common.wrap_action') + ' ' + this.$preciseTokenValue(this.swap.from_value) + ' ' + this.swap.from_token.symbol.toUpperCase() +
              ' ' + this.$t('common.to') + ' ' + this.swap.to_token.symbol.toUpperCase()
            )
            this.clearForm()
          })
          .catch((err) => {
            this.transaction.loading = false
            this.transaction.pending = false
            this.loading = false
            if (err.code === 4001) {
              if (this.$refs.confirmSwap !== undefined) {
                this.$store.dispatch('modal/changeModalGlobalState', {
                  state: true
                })
                this.$refs.confirmSwap.rejectTransaction({
                  type: 'denied'
                })
              }
            } else {
              const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
              if (this.$refs.confirmSwap !== undefined) {
                this.$store.dispatch('modal/changeModalGlobalState', {
                  state: true
                })
                this.$refs.confirmSwap.rejectTransaction({
                  type: 'error',
                  message: errorMessage
                })
              }
            }
          })
      } catch (err) {
        this.transaction.loading = false
        this.transaction.pending = false
        this.loading = false
        let errorMessage = this.$t('swap.error_text')
        if (err.message !== undefined && err.message !== null) {
          errorMessage = this.$t('swap.error_text_with_error', { error: this.$getRPCError(err.message) })
        }
        if (this.$refs.confirmSwap !== undefined) {
          this.$store.dispatch('modal/changeModalGlobalState', {
            state: true
          })
          this.$refs.confirmSwap.rejectTransaction({
            type: 'error',
            message: errorMessage
          })
        }
      }
    },
    async unwrapToken() {
      this.transaction.loading = true
      this.transaction.pending = true
      this.loading = true
      const chainOk = await this.$replaceChain()
      if (!chainOk) {
        this.transaction.loading = false
        this.transaction.pending = false
        this.loading = false
        return
      }
      const userAddress = await this.$getUserAddress()
      const WBNBFile = await import('~/assets/abis/WBNB.json')
      const WBNB = WBNBFile.default
      const wbnbContract = new window.eth.eth.Contract(WBNB, this.$config.wbnbAddress)
      const stringNumber = Number(parseFloat(this.swap.from_value)).toPrecision(10)
      const valueWei = window.eth.utils.toBN(
        window.eth.utils.toWei(
          stringNumber
        )
      )
      try {
        const currentAmount = await wbnbContract.methods.balanceOf(userAddress).call({ from: userAddress })
        if (window.eth.utils.toBN(currentAmount).gte(valueWei)) {
          const gasPrice = await this.$calculateGasCost()
          const gasFeeEstimated = await wbnbContract.methods.withdraw(valueWei)
            .estimateGas({ from: userAddress, gas: this.transaction.gasFee })

          wbnbContract.methods.withdraw(valueWei)
            .send({ from: userAddress, gas: Math.round(gasFeeEstimated * 1.15).toString(), gasPrice })
            .then((result) => {
              this.transaction.loading = false
              this.transaction.pending = false
              this.loading = false
              this.transaction.tx = result.transactionHash
              if (this.$refs.confirmSwap !== undefined) {
                this.$store.dispatch('modal/changeModalGlobalState', {
                  state: true
                })
                this.$refs.confirmSwap.initSuccessScreen()
              }
              this.$saveTransaction({
                tid0: this.$config.wbnbAddress,
                t0_amount: this.swap.from_value,
                hash: result.transactionHash,
                gasUsed: result.gasUsed,
                transactionIndex: result.transactionIndex
              },
              this.$t('common.unwrap_action') + ' ' + this.$preciseTokenValue(this.swap.from_value) + ' ' + this.swap.from_token.symbol.toUpperCase() +
              ' ' + this.$t('common.to') + ' ' + this.swap.to_token.symbol.toUpperCase()
              )
              this.clearForm()
            })
            .catch((err) => {
              this.transaction.loading = false
              this.transaction.pending = false
              this.loading = false
              if (err.code === 4001) {
                if (this.$refs.confirmSwap !== undefined) {
                  this.$store.dispatch('modal/changeModalGlobalState', {
                    state: true
                  })
                  this.$refs.confirmSwap.rejectTransaction({
                    type: 'denied'
                  })
                }
              } else {
                const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
                if (this.$refs.confirmSwap !== undefined) {
                  this.$store.dispatch('modal/changeModalGlobalState', {
                    state: true
                  })
                  this.$refs.confirmSwap.rejectTransaction({
                    type: 'error',
                    message: errorMessage
                  })
                }
              }
            })
        } else if (this.$refs.confirmSwap !== undefined) {
          this.$store.dispatch('modal/changeModalGlobalState', {
            state: true
          })
          this.$refs.confirmSwap.rejectTransaction({
            type: 'error',
            message: this.$t('swap.insufficient_wrapped_amount')
          })
          this.transaction.loading = false
          this.transaction.pending = false
          this.loading = false
        }
      } catch (err) {
        this.transaction.loading = false
        this.transaction.pending = false
        this.loading = false
        let errorMessage = this.$t('swap.error_text')
        if (err.message !== undefined && err.message !== null) {
          errorMessage = this.$t('swap.error_text_with_error', { error: this.$getRPCError(err.message) })
        }
        if (this.$refs.confirmSwap !== undefined) {
          this.changeModalGlobalState({
            state: true
          })
          this.$refs.confirmSwap.rejectTransaction({
            type: 'error',
            message: errorMessage
          })
        }
      }
    },
    async swapTokens() {
      this.transaction.loading = true
      const chainOk = await this.$replaceChain()
      if (!chainOk) {
        this.transaction.loading = false
        return
      }
      if (!this.$isStableCurrency(this.swap.from_token.symbol) &&
        !this.$isStableCurrency(this.swap.to_token.symbol)) {
        this.changeMethod('token-token')
      }
      if (!this.methodsAvailable.includes(this.swap.method)) {
        this.$toast.fire({
          icon: 'error',
          title: this.$t('swap.method_dont_exist')
        })
        this.transaction.loading = false
        return
      }
      if (this.$refs.confirmSwap !== undefined) {
        this.$refs.confirmSwap.initLoadingScreen()
      }
      switch (this.swap.method) {
        case 'swapExactETHForTokens':
        case 'swapETHForExactTokens':
          this.swapETHForTokens()
          break
        case 'swapTokensForExactETH':
        case 'swapExactTokensForETH':
          this.swapTokensForETH()
          break
        case 'swapTokensForExactTokens':
        case 'swapExactTokensForTokens':
          this.swapTokensForTokens()
          break
      }
    },
    async swapETHForTokens() {
      let amountOut = this.$calculateWithSlippage(this.swap.to_value, this.slippageTolerance, 'min', false)
      let amountIn = this.$calculateWithSlippage(this.swap.from_value, 0, 'min', false)

      amountOut = this.$toWeiDecimal(amountOut, this.swap.to_token.decimals)
      amountIn = this.$toWeiDecimal(amountIn, this.swap.from_token.decimals)

      if (this.swap.method === 'swapETHForExactTokens') {
        amountOut = this.$calculateWithSlippage(this.swap.to_value, 0, 'min', false)
        amountIn = this.$calculateWithSlippage(this.swap.from_value, this.slippageTolerance, 'max', false)

        amountOut = this.$toWeiDecimal(amountOut, this.swap.to_token.decimals)
        amountIn = this.$toWeiDecimal(amountIn, this.swap.from_token.decimals)
      }

      const pathAddresses = [
        this.$config.wbnbAddress,
        this.swap.to_token.address
      ]
      // if (!this.multihops) {
      //   pathAddresses.splice(1, 0, this.$config.busdAddress)
      // }

      const userAddress = await this.$getUserAddress()
      const PeriodRouterFile = await import('~/assets/abis/PeriodRouter.json')
      const PeriodRouter = PeriodRouterFile.default
      const routerContract = new window.eth.eth.Contract(PeriodRouter, this.$config.periodRouter)
      try {
        this.transaction.pending = true
        const gasPrice = await this.$calculateGasCost()
        const gasFeeEstimated = await routerContract.methods[this.swap.method](
          amountOut,
          pathAddresses,
          userAddress,
          this.$calculateDeadline(this.deadline)
        )
          .estimateGas({ from: userAddress, value: amountIn, gas: this.transaction.gasFee })
        await routerContract.methods[this.swap.method](
          amountOut,
          pathAddresses,
          userAddress,
          this.$calculateDeadline(this.deadline)
        ).send({ from: userAddress, value: amountIn, gas: Math.round(gasFeeEstimated * 1.15).toString(), gasPrice })
          .then((result) => {
            this.transaction.loading = false
            this.transaction.pending = false
            this.transaction.tx = result.transactionHash
            if (this.$refs.confirmSwap !== undefined) {
              this.$refs.confirmSwap.initSuccessScreen()
            }
            this.$saveTransaction({
              tid0: this.swap.from_token.address,
              tid1: this.swap.to_token.address,
              tid0_token: this.swap.from_token,
              tid1_token: this.swap.to_token,
              t0_amount: this.swap.from_value,
              t1_amount: this.swap.to_value,
              hash: result.transactionHash,
              gasUsed: result.gasUsed,
              transactionIndex: result.transactionIndex,
              type: 'swap'
            },
            this.$t('common.swap_action') + ' ' + this.$preciseTokenValue(this.swap.from_value) + ' ' + this.swap.from_token.symbol.toUpperCase() +
              ' ' + this.$t('common.for') + ' ' + this.$preciseTokenValue(this.swap.to_value) + ' ' + this.swap.to_token.symbol.toUpperCase()
            )
            this.clearForm()
          })
          .catch((err) => {
            this.transaction.pending = false
            this.transaction.loading = false
            if (err.code === 4001) {
              if (this.$refs.confirmSwap !== undefined) {
                this.$refs.confirmSwap.rejectTransaction({
                  type: 'denied'
                })
              }
            } else {
              const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
              if (this.$refs.confirmSwap !== undefined) {
                this.$refs.confirmSwap.rejectTransaction({
                  type: 'error',
                  message: errorMessage
                })
              }
            }
          })
      } catch (err) {
        this.transaction.pending = false
        this.transaction.loading = false
        let errorMessage = this.$t('swap.error_text')
        if (err.message !== undefined && err.message !== null) {
          errorMessage = this.$t('swap.error_text_with_error', { error: this.$getRPCError(err.message) })
        }
        if (this.$refs.confirmSwap !== undefined) {
          this.$refs.confirmSwap.rejectTransaction({
            type: 'error',
            message: errorMessage
          })
        }
      }
    },
    async swapTokensForETH() {
      let firstArgument = this.$calculateWithSlippage(this.swap.from_value, 0, 'max', false)
      let secondArgument = this.$calculateWithSlippage(this.swap.to_value, this.slippageTolerance, 'min', false)

      firstArgument = this.$toWeiDecimal(firstArgument, this.swap.from_token.decimals)
      secondArgument = this.$toWeiDecimal(secondArgument, this.swap.to_token.decimals)

      if (this.swap.method === 'swapTokensForExactETH') {
        firstArgument = this.$calculateWithSlippage(this.swap.to_value, 0, 'min', false)
        secondArgument = this.$calculateWithSlippage(this.swap.from_value, this.slippageTolerance, 'max', false)

        firstArgument = this.$toWeiDecimal(firstArgument, this.swap.to_token.decimals)
        secondArgument = this.$toWeiDecimal(secondArgument, this.swap.from_token.decimals)
      }

      const pathAddresses = [
        this.swap.from_token.address,
        this.$config.wbnbAddress
      ]
      // if (!this.multihops) {
      //   pathAddresses.splice(1, 0, this.$config.busdAddress)
      // }

      try {
        this.transaction.pending = true
        const userAddress = await this.$getUserAddress()
        const PeriodRouterFile = await import('~/assets/abis/PeriodRouter.json')
        const PeriodRouter = PeriodRouterFile.default
        const routerContract = new window.eth.eth.Contract(PeriodRouter, this.$config.periodRouter)
        const gasPrice = await this.$calculateGasCost()
        const gasFeeEstimated = await routerContract.methods[this.swap.method](
          firstArgument,
          secondArgument,
          pathAddresses,
          userAddress,
          this.$calculateDeadline(this.deadline)
        )
          .estimateGas({ from: userAddress, gas: this.transaction.gasFee })

        await routerContract.methods[this.swap.method](
          firstArgument,
          secondArgument,
          pathAddresses,
          userAddress,
          this.$calculateDeadline(this.deadline)
        ).send({ from: userAddress, gas: Math.round(gasFeeEstimated * 1.15).toString(), gasPrice })
          .then((result) => {
            this.transaction.pending = false
            this.transaction.loading = false
            this.transaction.tx = result.transactionHash
            if (this.$refs.confirmSwap !== undefined) {
              this.$refs.confirmSwap.initSuccessScreen()
            }
            this.$saveTransaction({
              tid0: this.swap.from_token.address,
              tid1: this.swap.to_token.address,
              tid0_token: this.swap.from_token,
              tid1_token: this.swap.to_token,
              t0_amount: this.swap.from_value,
              t1_amount: this.swap.to_value,
              hash: result.transactionHash,
              gasUsed: result.gasUsed,
              transactionIndex: result.transactionIndex,
              type: 'swap'
            },
            this.$t('common.swap_action') + ' ' + this.$preciseTokenValue(this.swap.from_value) + ' ' + this.swap.from_token.symbol.toUpperCase() +
              ' ' + this.$t('common.for') + ' ' + this.$preciseTokenValue(this.swap.to_value) + ' ' + this.swap.to_token.symbol.toUpperCase()
            )
            this.clearForm()
          })
          .catch((err) => {
            this.transaction.pending = false
            this.transaction.loading = false
            if (err.code === 4001) {
              if (this.$refs.confirmSwap !== undefined) {
                this.$refs.confirmSwap.rejectTransaction({
                  type: 'denied'
                })
              }
            } else {
              const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
              if (this.$refs.confirmSwap !== undefined) {
                this.$refs.confirmSwap.rejectTransaction({
                  type: 'error',
                  message: errorMessage
                })
              }
            }
          })
      } catch (err) {
        this.transaction.pending = false
        this.transaction.loading = false
        let errorMessage = this.$t('swap.error_text')
        if (err.message !== undefined && err.message !== null) {
          errorMessage = this.$t('swap.error_text_with_error', { error: this.$getRPCError(err.message) })
        }
        if (this.$refs.confirmSwap !== undefined) {
          this.$refs.confirmSwap.rejectTransaction({
            type: 'error',
            message: errorMessage
          })
        }
      }
    },
    async swapTokensForTokens() {
      let firstArgument = this.$calculateWithSlippage(this.swap.from_value, 0, 'max', false)
      let secondArgument = this.$calculateWithSlippage(this.swap.to_value, this.slippageTolerance, 'min', false)

      firstArgument = this.$toWeiDecimal(firstArgument, this.swap.from_token.decimals)
      secondArgument = this.$toWeiDecimal(secondArgument, this.swap.to_token.decimals)

      if (this.swap.method === 'swapTokensForExactTokens') {
        firstArgument = this.$calculateWithSlippage(this.swap.to_value, 0, 'min', false)
        secondArgument = this.$calculateWithSlippage(this.swap.from_value, this.slippageTolerance, 'max', false)
        firstArgument = this.$toWeiDecimal(firstArgument, this.swap.to_token.decimals)
        secondArgument = this.$toWeiDecimal(secondArgument, this.swap.from_token.decimals)
      }

      const pathAddresses = [
        this.$changeZeroToWrapped(this.swap.from_token.address),
        this.$changeZeroToWrapped(this.swap.to_token.address)
      ]
      // if (!this.multihops) {
      //   pathAddresses.splice(1, 0, this.$config.busdAddress)
      // }
      try {
        this.transaction.pending = true
        const userAddress = await this.$getUserAddress()
        const PeriodRouterFile = await import('~/assets/abis/PeriodRouter.json')
        const PeriodRouter = PeriodRouterFile.default
        const routerContract = new window.eth.eth.Contract(PeriodRouter, this.$config.periodRouter)
        const gasPrice = await this.$calculateGasCost()
        const gasFeeEstimated = await routerContract.methods[this.swap.method](
          firstArgument,
          secondArgument,
          pathAddresses,
          userAddress,
          this.$calculateDeadline(this.deadline)
        )
          .estimateGas({ from: userAddress, gas: this.transaction.gasFee })

        await routerContract.methods[this.swap.method](
          firstArgument,
          secondArgument,
          pathAddresses,
          userAddress,
          this.$calculateDeadline(this.deadline)
        ).send({ from: userAddress, gas: Math.round(gasFeeEstimated * 1.15).toString(), gasPrice })
          .then((result) => {
            this.transaction.pending = false
            this.transaction.loading = false
            this.transaction.tx = result.transactionHash
            if (this.$refs.confirmSwap !== undefined) {
              this.$refs.confirmSwap.initSuccessScreen()
            }
            this.$saveTransaction({
              tid0: this.swap.from_token.address,
              tid1: this.swap.to_token.address,
              tid0_token: this.swap.from_token,
              tid1_token: this.swap.to_token,
              t0_amount: this.swap.from_value,
              t1_amount: this.swap.to_value,
              hash: result.transactionHash,
              gasUsed: result.gasUsed,
              transactionIndex: result.transactionIndex,
              type: 'swap'
            },
            this.$t('common.swap_action') + ' ' + this.$preciseTokenValue(this.swap.from_value) + ' ' + this.swap.from_token.symbol.toUpperCase() +
              ' ' + this.$t('common.for') + ' ' + this.$preciseTokenValue(this.swap.to_value) + ' ' + this.swap.to_token.symbol.toUpperCase()
            )
            this.clearForm()
          })
          .catch((err) => {
            this.transaction.pending = false
            this.transaction.loading = false
            if (err.code === 4001) {
              if (this.$refs.confirmSwap !== undefined) {
                this.$refs.confirmSwap.rejectTransaction({
                  type: 'denied'
                })
              }
            } else {
              const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
              if (this.$refs.confirmSwap !== undefined) {
                this.$refs.confirmSwap.rejectTransaction({
                  type: 'error',
                  message: errorMessage
                })
              }
            }
          })
      } catch (err) {
        this.transaction.pending = false
        this.transaction.loading = false
        let errorMessage = this.$t('swap.error_text')
        if (err.message !== undefined && err.message !== null) {
          errorMessage = this.$t('swap.error_text_with_error', { error: this.$getRPCError(err.message) })
        }
        if (this.$refs.confirmSwap !== undefined) {
          this.$refs.confirmSwap.rejectTransaction({
            type: 'denied',
            message: errorMessage
          })
        }
      }
    },
    checkLiquidity() {
      const fromValue = parseFloat(this.swap.from_value)
      if (this.pairData !== null && this.swap.from_value !== null && this.swap.to_value !== null) {
        if (
          this.pairData.totalSupply === 0 ||
          this.pairData.reserves.reserve0 < fromValue ||
          this.pairData.reserves.reserve1 < this.swap.to_value
        ) {
          this.errorText = this.$t('swap.insufficient_liquidity')
          this.swapCtaDisabled = true
          return
        }
        const initialMarketPrice = this.pairData.reserves.reserve0 / this.pairData.reserves.reserve1
        const conductProduct = this.pairData.reserves.reserve0 * this.pairData.reserves.reserve1
        const amountInWithFee = fromValue + fromValue * this.$config.fee
        const newFromAmount = this.pairData.reserves.reserve0 + amountInWithFee
        const newToAmount = conductProduct / newFromAmount
        const toReceive = this.pairData.reserves.reserve1 - newToAmount
        const newPricePerTo = amountInWithFee / toReceive
        const priceImpact = (newPricePerTo - initialMarketPrice) / initialMarketPrice * 100
        if (this.$ratePriceImpact(priceImpact) === 'high') {
          this.errorText = this.$t('swap.price_impact_high')
          this.swapCtaDisabled = true
        } else {
          this.normalizeCta()
        }
      } else {
        this.swapCtaDisabled = true
      }
    },
    checkInsufficientFunds() {
      if (this.swap.from_value !== null && parseFloat(this.swap.from_value) === 0 && this.swap.to_value !== null) {
        this.errorText = this.$t('swap.insufficient', { symbol: this.swap.from_token.symbol.toUpperCase() })
        this.swapInsufficient = true
      } else if (this.swap.to_value !== null && parseFloat(this.swap.to_value) === 0 && this.swap.from_value !== null) {
        this.errorText = this.$t('swap.insufficient', { symbol: this.swap.to_token.symbol.toUpperCase() })
        this.swapInsufficient = true
      } else {
        this.swapInsufficient = false
      }
    },
    changeModalTitle(titleType) {
      switch (titleType) {
        case 'loading':
          this.modalTitle = this.$t('swap.waiting_for_confirmation')
          break
        case 'success':
          this.modalTitle = this.$t('swap.success')
          break
        case 'error':
          this.modalTitle = this.$t('swap.error_header')
          break
        default:
          this.modalTitle = this.$t('swap.confirm_swap')
      }
    },
    normalizeCta() {
      this.errorText = ''
      this.swapCtaDisabled = false
    },
    copyAddress(evt, address) {
      this.$copy(evt, address)
      if (evt.target.classList.contains('svg-area')) {
        evt.target.setAttribute('tooltip', this.$t('common.copied'))
        setTimeout(() => {
          evt.target.setAttribute('tooltip', this.$t('common.copy'))
        }, 1000)
      } else {
        const item = evt.target.closest('.svg-area')
        item.setAttribute('tooltip', this.$t('common.copied'))
        setTimeout(() => {
          item.setAttribute('tooltip', this.$t('common.copy'))
        }, 1000)
      }
    },
    setDateFilter(days) {
      if (this.dateFilter !== days && days > 0 && days <= 365) {
        this.dateFilter = days
        this.loadChart()
      }
    },
    loadChart() {
      if (this.openChart) {
        this.loadingChart = true
        if (this.swap.from_token.address.toLowerCase() === this.$config.periodToken.toLowerCase() && this.swap.to_token.address === this.$config.ZERO) {
          const chartColor = !this.darkTheme ? 'rgb(60, 222, 235)' : 'rgb(255, 60, 159)'
          this.lineChart = {
            data: {
              datasets: [
                {
                  borderWidth: 2,
                  radius: 0,
                  fill: false,
                  data: [1.382, 1.589, 2.175, 1.474, 0.683, 0.914, 1.374, 1.518],
                  borderColor: chartColor
                }
              ]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              aspectRatio: 1.5,
              interaction: {
                intersect: false
              },
              plugins: {
                legend: false
              },
              scales: {
                x: {
                  type: 'linear'
                }
              }
            }
          }
        } else {
          this.lineChart = null
        }
        setTimeout(() => {
          this.loadingChart = false
        }, 1300)
      }
    },
    closeModal() {
      this.changeModalGlobalState({
        state: false
      })
    },
    handleClicks(evt) {
      if (
        this.$refs.fromListButton !== undefined &&
        this.$refs.fromList !== undefined &&
        !this.$refs.fromList.contains(evt.target) &&
        !this.$refs.fromListButton.contains(evt.target) &&
        !this.$refs.manageFromArea.$el.contains(evt.target) &&
        !this.$refs.manageFromButton.contains(evt.target) &&
        this.$refs.fromList.classList.contains('active') &&
        !evt.target.classList.contains('remove-list')
      ) {
        this.toggleFromList()
      }
      if (
        this.$refs.manageFromButton !== undefined &&
        this.$refs.manageFromArea !== undefined &&
        !this.$refs.manageFromArea.$el.contains(evt.target) &&
        !this.$refs.manageFromButton.contains(evt.target) &&
        this.$refs.manageFromArea.$el.classList.contains('active') &&
        !evt.target.classList.contains('remove-list')
      ) {
        this.toggleManageFrom()
      }
      if (
        this.$refs.toListButton !== undefined &&
        this.$refs.toList !== undefined &&
        !this.$refs.toList.contains(evt.target) &&
        !this.$refs.toListButton.contains(evt.target) &&
        !this.$refs.manageToArea.$el.contains(evt.target) &&
        !this.$refs.manageToButton.contains(evt.target) &&
        this.$refs.toList.classList.contains('active') &&
        !evt.target.classList.contains('remove-list')
      ) {
        this.toggleToList()
      }
      if (
        this.$refs.manageToButton !== undefined &&
        this.$refs.manageToArea !== undefined &&
        !this.$refs.manageToArea.$el.contains(evt.target) &&
        !this.$refs.manageToButton.contains(evt.target) &&
        this.$refs.manageToArea.$el.classList.contains('active') &&
        !evt.target.classList.contains('remove-list')
      ) {
        this.toggleManageTo()
      }
    }
  }
}
</script>

<style scoped>
  @import url('~/assets/css/new/swap.css');
  @import url('~/assets/css/new/tokenSelectorWide.css');
</style>
